import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

ChartJS.register(ArcElement, Tooltip, Legend);

const HdfcSipCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
        {
            "question": "What is an HDFC Mutual Fund SIP Calculator?",
            "answer": "The HDFC Mutual Fund SIP Calculator is an online financial tool that helps investors estimate the maturity value of their mutual fund investments made via SIPs. By entering variables such as monthly investment amount, expected return rate, and tenure, users can calculate the future value of their investments instantly."
          },
          {
            "question": "Is the HDFC SIP calculator free to use?",
            "answer": "Yes, the HDFC SIP calculator is absolutely free. Anyone can access and use it to plan their mutual fund investments more effectively without any hidden charges or subscription requirements."
          },
          {
            "question": "What details do I need to use the SIP calculator?",
            "answer": "To use the calculator, you typically need to input the monthly SIP amount, the expected annual rate of return (in percentage), and the investment duration (in months or years). Optionally, some calculators also allow you to select frequency (monthly/quarterly) and target amount goals."
          },
          {
            "question": "Can I use the SIP calculator to plan for a future financial goal?",
            "answer": "Yes, you can. The calculator also allows reverse calculation, where you enter a target amount, and it tells you how much you need to invest every month to reach that goal within your desired tenure and expected return rate."
          },
          {
            "question": "How accurate is the SIP calculator in predicting returns?",
            "answer": "The SIP calculator provides an estimate based on the inputs you provide, particularly the expected rate of return. Since mutual fund returns are subject to market volatility, the actual returns may vary. Hence, the calculator gives you a close approximation but not a guaranteed figure."
          },
          {
            "question": "What is the formula used in the SIP calculator?",
            "answer": "The SIP calculator uses the compound interest formula:  M = P × [(1 + i)<sup>n</sup> - 1] / i × (1 + i) Where M is the maturity amount, P is the monthly investment, i is the periodic interest rate (annual return divided by 12), and n is the total number of SIPs."
          },
          {
            "question": "What is the minimum amount required to start a SIP in HDFC Mutual Fund?",
            "answer": "You can start a SIP in HDFC Mutual Fund with as little as ₹500 per month. This makes it accessible for investors with limited budgets to begin their investment journey."
          },
          {
            "question": "Can I change the SIP amount or tenure later?",
            "answer": "Yes, most mutual fund houses, including HDFC, allow you to modify your SIP amount and tenure at any point. However, you may need to initiate a new SIP or stop the current one depending on the platform’s flexibility."
          },
          {
            "question": "What is the maximum tenure I can set in the SIP calculator?",
            "answer": "While there's no fixed upper limit in most calculators, you can typically calculate returns for up to 30 or 35 years. This allows long-term investors to plan for major life goals like retirement, child’s education, or buying a home."
          },
          {
            "question": "Can I use the calculator for SIPs in ELSS Mutual Funds?",
            "answer": "Absolutely. You can use the SIP calculator to estimate returns from any type of mutual fund, including ELSS (Equity Linked Savings Scheme) funds. However, keep in mind that ELSS comes with a 3-year lock-in period."
          },
          {
            "question": "What if I miss a SIP payment—how will it affect the calculator’s result?",
            "answer": "The calculator assumes consistent monthly investments. If you skip a SIP, your actual returns may fall short of the estimated maturity value. It's advisable to maintain regularity in SIPs to achieve your financial goals effectively."
          },
          {
            "question": "How does the rate of return affect SIP earnings?",
            "answer": "The expected return rate plays a critical role in determining the maturity value. Even a small difference in return percentage can significantly impact your corpus, especially over a long investment horizon due to compounding effects."
          },
          {
            "question": "Can I use the SIP calculator for other fund houses besides HDFC?",
            "answer": "Yes, the formula used is universal. Although branded for HDFC, the SIP calculator can be used for estimating returns from any mutual fund SIP, regardless of the AMC (Asset Management Company)."
          },
          {
            "question": "Are SIP returns taxable?",
            "answer": "Yes, SIP returns are subject to capital gains tax. For equity mutual funds, gains are taxed at 10% if held for more than 1 year (LTCG) and 15% if sold within a year (STCG). For debt mutual funds, taxation varies based on tenure and type of fund. The calculator does not account for taxes."
          },
          {
            "question": "Can I download or save my SIP calculation results?",
            "answer": "Most online SIP calculators, including HDFC’s, allow you to download, email, or print your results for future reference. This helps in comparing multiple investment scenarios and tracking your planning history."
          }
        ]
    
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
      <Helmet>
        <title>
        HDFC SIP Calculator – Plan Your Mutual Fund Investments Easily
        </title>
        <meta
          name="description"
          content="Use the HDFC SIP Calculator to estimate your mutual fund returns. Plan your SIP investments smartly with accurate growth projections and savings insights."
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/hdfc-sip-calculator"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 "> HDFC SIP Calculator</span>
          </li>
        </ol>
        
      </nav> */}
          <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
        <AdBanner displayOrder="1" />
        </div>
      </div>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
            <h1 className="text-2xl font-bold mb-4">HDFC SIP Returns Calculator</h1>
        
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            HDFC Mutual Fund SIP Calculator
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Investing in mutual funds is a smart and strategic way to build long-term wealth, especially when done consistently over time. Among the most popular methods of investing in mutual funds is the Systematic Investment Plan (SIP) — a disciplined investment approach that allows investors to contribute a fixed sum at regular intervals, typically monthly. The HDFC Mutual Fund SIP Calculator is a powerful financial planning tool designed to help investors estimate the future value of their investments based on inputs like investment amount, expected returns, and tenure.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is SIP and Why Is It Popular?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A SIP is not a type of mutual fund; instead, it’s a method of investing in mutual funds gradually over time. Rather than making a one-time lump-sum investment, investors can choose to invest smaller amounts at regular intervals — weekly, monthly, or quarterly. This systematic approach brings financial discipline, promotes saving habits, and reduces the burden of market timing. It also leverages the benefits of rupee cost averaging and compounding, making it especially useful for long-term financial goals such as retirement, education, homeownership, or wealth accumulation.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              HDFC, being one of India’s leading financial institutions, offers a wide range of mutual fund schemes that are SIP-enabled. Whether you are investing in an Equity Linked Savings Scheme (ELSS) for tax benefits or in diversified equity funds for long-term growth, SIPs make your investment journey more manageable and methodical. To make this experience even more intuitive, HDFC provides access to a dedicated SIP calculator, which can help investors visualize potential returns and plan their investments more accurately.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can an HDFC SIP Calculator Help You?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The HDFC SIP Calculator is more than just a number-crunching tool. It acts as a personalized financial advisor that provides valuable insights into how your periodic investments can grow over time. By entering just a few details — monthly investment, expected annual return, and investment tenure — users can get a real-time estimate of the total invested amount, expected gains, and the maturity value of their investment.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Here are some of the top benefits of using the HDFC SIP Calculator:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Clear Financial Planning:</strong> The calculator helps you plan your future finances by estimating how much you need to invest to reach a particular financial goal.</li>
              <li><strong>Quick and Accurate Estimates:</strong> It saves time and effort by providing instant results, removing the need for complex manual calculations.</li>
              <li><strong>Goal-Oriented Investing:</strong> Whether you're saving for your child's education or your dream house, the tool helps you determine the SIP amount required to achieve that goal within a set timeframe.</li>
              <li><strong>Return Visualization:</strong> It provides a graphical and numerical representation of how your investment is expected to grow, including a breakdown of total principal and earned interest.</li>
              <li><strong>Risk-Adjusted Forecasting:</strong> By allowing changes in expected return rates, you can model best-case and worst-case scenarios depending on the prevailing market conditions.</li>
              <li><strong>Informed Decision-Making:</strong> It empowers you to make smarter investment choices by showing how small increases in SIP amount or tenure can significantly boost your returns.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the HDFC Mutual Fund SIP Calculator Work?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The HDFC Mutual Fund SIP Calculator operates using a compound interest formula that estimates the maturity value of SIP investments:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula:</strong><br />
           
             M = P × [(1 + i)<sup>n</sup> - 1] / i × (1 + i)
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>M = Maturity amount</li>
              <li>P = SIP amount invested per installment</li>
              <li>i = Expected return rate per period (annual rate ÷ 12)</li>
              <li>n = Total number of SIP payments</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Let’s consider an example to understand this better.<br />
              You invest ₹2,000 per month for a period of 12 months with an expected return rate of 14% annually (or roughly 1.17% monthly).<br />
              M = 2,000 × [(1 + 0.0117)<sup>12</sup> - 1] / 0.0117 × (1 + 0.0117)<br />
              M ≈ ₹25,900<br />
              This means, after one year of disciplined SIP contributions of ₹2,000 per month, your total investment of ₹24,000 would grow to approximately ₹25,900, assuming constant returns. This example highlights how even small monthly investments can yield meaningful growth over time.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the HDFC SIP Calculator Online
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the online SIP calculator is straightforward and hassle-free. Whether you are a seasoned investor or a beginner, the interface is designed to simplify the process:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Open the HDFC SIP Calculator tool – You can access it through platforms like TheFinMaster or directly via HDFC Mutual Fund’s website.</li>
              <li>Enter Your Monthly SIP Amount – For example, ₹4,000.</li>
              <li>Input the Expected Rate of Return – Let’s say 12% annually.</li>
              <li>Select Your Investment Tenure – Suppose it’s 5 years.</li>
              <li>View Your Results Instantly – The calculator will display:
                <ul className="list-disc pl-6 mt-2">
                  <li>Total investment: ₹2,40,000</li>
                  <li>Estimated returns: ₹89,945</li>
                  <li>Maturity value: ₹3,29,945</li>
                </ul>
              </li>
            </ol>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              You can also toggle to a Goal-Based Calculator, where instead of entering a monthly amount, you input your desired corpus (e.g., ₹7,50,000) and get an estimate of the monthly SIP needed (e.g., ₹12,396 over 4 years at 11% returns).
            </p>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Advantages of Using TheFinmaster’s SIP Calculator
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SIP calculator at TheFinMaster offers advanced features and a smoother user experience that stands out:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Intuitive Interface:</strong> Simple, clean sliders and input boxes make calculations quick.</li>
              <li><strong>Goal Tracking:</strong> You can switch between wealth creation and goal-based planning modes.</li>
              <li><strong>Instant Graphical Insights:</strong> Visual charts showcase your investment breakdown.</li>
              <li><strong>Customized Scenarios:</strong> Compare multiple scenarios by changing tenure and return rates.</li>
              <li><strong>No Login Required:</strong> Start calculating without any mandatory signup.</li>
              <li><strong>Educational Guides:</strong> The calculator is integrated with learning resources that explain SIP investing, fund selection, risk factors, and more.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Which Factors Influence SIP Earnings?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              SIP returns are not fixed. Various dynamic elements can impact how your investments grow:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Monthly Instalment Amount</strong><br />
                The higher your SIP amount, the greater your investment base. Even a small increase in monthly contributions (e.g., ₹500 more) can result in lakhs more at maturity due to compounding.</li>
              <li><strong>Investment Tenure</strong><br />
                Time plays a crucial role. SIPs are ideal for long-term investing. Staying invested for 10+ years helps ride market volatility and benefit from compounding.</li>
              <li><strong>Expected Rate of Return</strong><br />
                This depends on the performance of the mutual fund and market conditions. Equity funds typically offer higher returns but come with more risk, while debt funds are more stable but offer lower growth.</li>
              <li><strong>Market Conditions</strong><br />
                Mutual funds are market-linked. The returns can fluctuate based on macroeconomic trends, interest rates, inflation, and corporate performance. Long-term SIPs help average out these fluctuations.</li>
              <li><strong>Fund Type and Strategy</strong><br />
                Different mutual funds follow varied strategies — large-cap, mid-cap, small-cap, sectoral, hybrid, etc. Your choice of fund will impact both the risk and return potential.</li>
              <li><strong>Taxation</strong><br />
                While ELSS SIPs offer tax benefits under Section 80C, most equity fund gains are taxed at 10% beyond ₹1 lakh of annual gains (LTCG), and short-term gains are taxed at 15%. This affects your post-tax returns.</li>
            </ol>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
               {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
             {/* FAQ section */}
    <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default HdfcSipCalculator;
